import React, { useState, useRef, type ChangeEvent, useEffect } from "react";
import {
  Typography,
  Button,
  List,
  ListItem,
  ListItemText,
  Paper,
  Avatar,
  ListItemAvatar,
  Alert,
} from "@mui/material";

// Define the interface for the component props
interface FileUploadProps {
  onFilesChange: (files: File[]) => void;
  maxFiles?: number;
  maxFileSize?: number; // in bytes
  allowedExtensions?: string[];
  title?: string | React.ReactNode;
  buttonText?: string;
  restrictionText?: string;
  files?: File[];
  capture?: "user" | "environment";
}

export default function FileUpload({
  onFilesChange,
  maxFiles = 4,
  maxFileSize = 10 * 1024 * 1024, // 10MB default
  allowedExtensions = ["jpg", "jpeg", "png", "webp"],
  title = "画像ファイルのアップロード",
  buttonText = "ファイルを選択",
  restrictionText,
  files,
  capture,
}: FileUploadProps) {
  // State for file uploads
  const [uploadedFiles, setUploadedFiles] = useState<File[]>(files || []);
  const [previewUrls, setPreviewUrls] = useState<string[]>([]);
  const [errorMessage, setErrorMessage] = useState<string>("");
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Generate preview URLs when files change
  useEffect(() => {
    // Clean up previous URLs to avoid memory leaks
    previewUrls.forEach((url) => URL.revokeObjectURL(url));

    // Create new preview URLs
    const urls = uploadedFiles.map((file) => URL.createObjectURL(file));
    setPreviewUrls(urls);

    // Clean up function to release object URLs when component unmounts
    return () => {
      urls.forEach((url) => URL.revokeObjectURL(url));
    };
  }, [uploadedFiles]);

  // File upload handler
  const handleFileUpload = (event: ChangeEvent<HTMLInputElement>) => {
    setErrorMessage("");

    const files = event.target.files;
    if (!files) return;

    // Convert FileList to array for easier processing
    const fileArray = Array.from(files);

    // Check if adding these files would exceed the maximum
    if (uploadedFiles.length + fileArray.length > maxFiles) {
      setErrorMessage(
        `最大${maxFiles}ファイルまでしかアップロードできません。現在${uploadedFiles.length}ファイルがあるため、あと${maxFiles - uploadedFiles.length}ファイルまで追加できます。`,
      );

      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
      return;
    }

    // ファイルの検証を一度の処理に統合
    const invalidFiles = fileArray.filter((file) => {
      const isOversized = file.size > maxFileSize;
      const extension = file.name.split(".").pop()?.toLowerCase() || "";
      const hasInvalidExtension = !allowedExtensions.includes(extension);

      return isOversized || hasInvalidExtension;
    });

    // 問題があるファイルがある場合、分類してエラーメッセージを生成
    if (invalidFiles.length > 0) {
      const oversizedFiles = invalidFiles.filter(
        (file) => file.size > maxFileSize,
      );
      const invalidExtFiles = invalidFiles.filter((file) => {
        const extension = file.name.split(".").pop()?.toLowerCase() || "";
        return !allowedExtensions.includes(extension);
      });

      let errorMessages = [];

      // サイズエラーメッセージの生成
      if (oversizedFiles.length > 0) {
        const maxSizeMB = (maxFileSize / (1024 * 1024)).toFixed(0);
        const fileNames = oversizedFiles
          .map((file) => `「${file.name}」`)
          .join("\n");
        errorMessages.push(
          `ファイルサイズが大きすぎるため、以下のファイルがアップロードされませんでした。制限サイズは${maxSizeMB}MBです：\n${fileNames}`,
        );
      }

      // 拡張子エラーメッセージの生成
      if (invalidExtFiles.length > 0) {
        const fileNames = invalidExtFiles
          .map((file) => `「${file.name}」`)
          .join("\n");
        errorMessages.push(
          `以下のファイルは許可された形式ではないためアップロードされませんでした：\n${fileNames}`,
        );
      }

      // エラーメッセージの設定
      setErrorMessage(errorMessages.join("\n\n"));

      // すべてのファイルが無効な場合、入力をリセットして終了
      if (invalidFiles.length === fileArray.length) {
        if (fileInputRef.current) {
          fileInputRef.current.value = "";
        }
        return;
      }
    }

    // Filter files based on all restrictions
    const validFiles = fileArray.filter((file) => {
      // Check file extension
      const extension = file.name.split(".").pop()?.toLowerCase() || "";
      const isValidExtension = allowedExtensions.includes(extension);

      // Check file size
      const isValidSize = file.size <= maxFileSize;

      return isValidExtension && isValidSize;
    });

    // Limit to maximum number of files
    const newFiles = [...uploadedFiles, ...validFiles].slice(0, maxFiles);

    setUploadedFiles(newFiles);
    onFilesChange(newFiles);

    // Reset file input
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  // Handle file removal
  const handleRemoveFile = (index: number) => {
    const newFiles = [...uploadedFiles];
    newFiles.splice(index, 1);
    setUploadedFiles(newFiles);
    onFilesChange(newFiles);

    // Clear error message when files are removed
    if (errorMessage) {
      setErrorMessage("");
    }
  };

  // Trigger file input click
  const handleUploadButtonClick = () => {
    setErrorMessage("");

    if (fileInputRef.current) {
      // 通常のアップロードではcapture属性を削除
      fileInputRef.current.removeAttribute("capture");
      fileInputRef.current.click();
    }
  };

  // カメラ起動ボタンのクリックハンドラ
  const handleCameraButtonClick = () => {
    setErrorMessage("");

    if (fileInputRef.current) {
      // バックカメラを使用するようにcapture属性を設定
      fileInputRef.current.setAttribute("capture", "environment");
      fileInputRef.current.click();
    }
  };

  // Generate default restriction text if not provided
  const defaultRestrictionText = `※ ${allowedExtensions.join(", ")}形式のみ、1ファイル${(maxFileSize / (1024 * 1024)).toFixed(0)}MBまで、最大${maxFiles}ファイルまで`;

  // Display uploaded files
  return (
    <>
      {typeof title === "string" ? (
        <Typography variant="subtitle1" sx={{ mt: 2, mb: 1 }}>
          {title}
        </Typography>
      ) : (
        title
      )}
      <input
        type="file"
        capture={capture}
        accept={allowedExtensions.map((ext) => `.${ext}`).join(",")}
        multiple
        ref={fileInputRef}
        onChange={handleFileUpload}
        style={{ display: "none" }}
      />
      <Button
        variant="outlined"
        fullWidth
        onClick={handleUploadButtonClick}
        disabled={uploadedFiles.length >= maxFiles}
        sx={{ mt: 1 }}
      >
        {buttonText}
      </Button>
      <Button
        variant="outlined"
        fullWidth
        onClick={handleCameraButtonClick}
        disabled={uploadedFiles.length >= maxFiles}
        sx={{ mt: 1, mb: 1 }}
      >
        カメラで撮影する
      </Button>
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ display: "block", mt: 1 }}
      >
        {restrictionText || defaultRestrictionText}
      </Typography>

      {/* エラーメッセージを表示 */}
      {errorMessage && (
        <Alert severity="error" sx={{ mt: 1, whiteSpace: "pre-line" }}>
          {errorMessage}
        </Alert>
      )}

      {/* Display uploaded files with previews */}
      {uploadedFiles.length > 0 && (
        <Paper variant="outlined" sx={{ mt: 2, p: 1 }}>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            添付ファイル ({uploadedFiles.length}/{maxFiles})
          </Typography>
          <List dense>
            {uploadedFiles.map((file, index) => (
              <ListItem
                key={index}
                secondaryAction={
                  <Button
                    size="small"
                    color="error"
                    onClick={() => handleRemoveFile(index)}
                  >
                    削除
                  </Button>
                }
              >
                <ListItemAvatar>
                  <Avatar
                    variant="rounded"
                    src={previewUrls[index]}
                    sx={{ width: 56, height: 56 }}
                    alt={`${file.name}のプレビュー`}
                  />
                </ListItemAvatar>
                <ListItemText
                  primary={file.name}
                  secondary={`${(file.size / (1024 * 1024)).toFixed(2)} MB`}
                  sx={{ ml: 1 }}
                />
              </ListItem>
            ))}
          </List>
        </Paper>
      )}
    </>
  );
}
